import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { IoBedOutline, IoPersonOutline } from 'react-icons/io5';
import { MdBalcony, MdOutlineKingBed } from 'react-icons/md';
import { FaRupeeSign } from 'react-icons/fa';
import BookingModal from './BookingModal';
import LOTUS_IMG from '../assets/LOTUS.jpg';
import ORCHID_IMG from '../assets/ORCHID.jpg';
import GLADIOLUS_IMG from '../assets/Gladiolus.JPG';
import JASMINE_IMG from '../assets/JASMINE.jpg';

const Rooms = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');

  const rooms = [
    {
      title: "Lotus",
      image: LOTUS_IMG,
      price: "Contact for Price",
      beds: "1 Double Bed",
      guests: "2-4 Guests",
      features: ["Spacious Room", "Modern Amenities", "En-suite Bathroom", "WiFi"],
      popular: false
    },
    {
      title: "Orchid",
      image: ORCHID_IMG,
      price: "Contact for Price",
      beds: "2 Bedroom Apartment",
      guests: "4-6 Guests",
      features: ["Double Bed", "Full Apartment", "Kitchen Access", "Living Area"],
      popular: true
    },
    {
      title: "Gladiolus",
      image: GLADIOLUS_IMG,
      price: "Contact for Price",
      beds: "Independent Room",
      guests: "1-2 Guests",
      features: ["Private Space", "Perfect for Solo Travelers", "Cozy Setup", "WiFi"],
      popular: false
    },
    {
      title: "Jasmine",
      image: JASMINE_IMG,
      price: "Contact for Price",
      beds: "1 Bedroom Apartment",
      guests: "4-5 Guests",
      features: ["Full Apartment", "Kitchen Facilities", "Comfortable Stay", "Modern Amenities"],
      popular: false
    }
  ];

  return (
    <section id="rooms" className="section-padding bg-gradient-to-b from-primary-50 to-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-block mb-4"
          >
            <IoBedOutline className="text-primary-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Room Types & Stay Options
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose from our carefully designed rooms and suites, each offering unique views and comfort
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {rooms.map((room, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {/* Popular Badge */}
              {room.popular && (
                <div className="absolute top-4 right-4 z-10 bg-accent-500 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                  Most Popular
                </div>
              )}

              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <motion.img
                  src={room.image}
                  alt={room.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                
                {/* Price Tag */}
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg">
                  {room.price === "Contact for Price" ? (
                    <span className="text-sm font-bold text-gray-900">{room.price}</span>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <FaRupeeSign className="text-primary-600 text-sm" />
                      <span className="text-2xl font-bold text-gray-900">{room.price}</span>
                      <span className="text-sm text-gray-600">/night</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {room.title}
                </h3>

                {/* Room Details */}
                <div className="flex items-center space-x-6 mb-4 text-gray-600">
                  <div className="flex items-center space-x-2">
                    <MdOutlineKingBed className="text-primary-500 text-xl" />
                    <span className="text-sm">{room.beds}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <IoPersonOutline className="text-primary-500 text-xl" />
                    <span className="text-sm">{room.guests}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {room.features.map((feature, i) => (
                    <span
                      key={i}
                      className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex space-x-3">
                  <button 
                    onClick={() => {
                      setSelectedRoom(room.title);
                      setIsModalOpen(true);
                    }}
                    className="flex-1 btn-primary text-center text-sm py-2"
                  >
                    Book Now
                  </button>
                </div>
              </div>

              {/* Decorative Corner */}
              <div className="absolute top-0 left-0 w-20 h-20 bg-primary-500/10 rounded-br-full transform -translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500"></div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary-500 to-nature-leaf text-white rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold mb-3">Need Help Choosing?</h3>
          <p className="mb-4">Contact us to learn more about our rooms and get the best rates for your stay</p>
          <a href="#booking" className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-full font-medium inline-block transition-all hover:scale-105">
            Get in Touch
          </a>
        </motion.div>
      </div>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        roomTitle={selectedRoom}
      />
    </section>
  );
};

export default Rooms;
